import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  CarouselProvider, Slider, Slide, ButtonBack, ButtonNext,
} from 'pure-react-carousel';
import { Button, Icon } from '@resy/ui-kitchen';
import AmexCenturionEventCard from '../AmexCenturionNYEventCard';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';

import './AmexCenturionNYRecurringEvents.scss';

const AmexCenturionNYRecurringEvents = (
  {
    awsFolderName,
    baseRequestUrl,
    eventButtons,
    eventDetails = {},
    recurringJSONEvents,
    matchMedia,
    handleTagging,
  }
) => {
  const {
    recurring: {
      title = '',
      sub_title: subTitle = '',
      info_copy: infoCopy = '',
      fine_print_info_copy: finePrintInfoCopy = '',
      fine_print: finePrint = '',
      sr_title: srTitle = '',
      sr_info: srInfo = '',
      sr_controls_aria: srControlsAria = '',
      show_max_count: showMaxCount,
      show_more: showMore,
      more_button_label: showMoreLabel = '',
      active_button: activeButton = '',
      number_of_events: {
        custom_image: customImage = 2,
      } = {},
    } = {},
  } = eventDetails;
  const sanitizedFinePrint = sanitizeString(finePrint);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedSubTitle = sanitizeString(subTitle);
  const sanitizedInfoCopy = sanitizeString(infoCopy);
  const sanitizedFinePrintInfoCopy = sanitizeString(finePrintInfoCopy);

  if (Object.keys(eventDetails).length === 0) return null;
  const [events, setEvents] = useState(recurringJSONEvents.slice(0, showMaxCount));
  const [hideShowMoreButton, setHideShowMoreButton] = useState(false);

  const carouselActiveButton = `${baseRequestUrl}${awsFolderName}/images/${activeButton}`;

  const showMoreEvents = () => {
    if (showMore === 0 || showMore === -1) {
      setEvents(recurringJSONEvents);
    } else {
      const eventLength = parseInt(showMaxCount + showMore, 10);
      setEvents(recurringJSONEvents.slice(0, eventLength));
    }
    const lastEventItem = document.getElementById('mobileRecurringEventsList').lastChild;
    setTimeout(lastEventItem.focus, 0);
    setHideShowMoreButton(true);
  };

  const slidesNumber = matchMedia.lg ? 3 : 2;

  useEffect(() => {
    setEvents(recurringJSONEvents.slice(0, showMaxCount));
    if (!showMoreLabel) setHideShowMoreButton(true);
  }, []);

  const renderFinePrintLine = () => (
    <p
      className="AmexCenturionNYRecurringEvents__info"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: sanitizedFinePrint,
      }}
    />
  );

  const renderButtons = () => (
    <div
      role="group"
      aria-label={srControlsAria}
      className="AmexCenturionNYRecurringEvents__controls"
    >
      <ButtonBack
        aria-label="Previous slide"
        className="AmexCenturionNYRecurringEvents__controls--buttons back"
      >
        <Icon
          url={carouselActiveButton}
          aria-hidden="true"
        />
      </ButtonBack>
      <ButtonNext
        aria-label="Next slide"
        className="AmexCenturionNYRecurringEvents__controls--buttons next"
      >
        <Icon
          url={carouselActiveButton}
          aria-hidden="true"
        />
      </ButtonNext>
    </div>
  );

  const renderEventGrid = () => {
    const recurringEvents = matchMedia.md ? recurringJSONEvents : events;
    return (
      <>
        <ul
          id="mobileRecurringEventsList"
          className={cx('AmexCenturionNYRecurringEvents__grid', {
            'layout-single': (recurringEvents.length === 1),
            'layout-multiple list-2-cards': (recurringEvents.length === 2),
            'layout-multiple list-3-cards': (recurringEvents.length === 3),
          })}
        >
          {recurringEvents.map((event) => (
            <li
              tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
            >
              <AmexCenturionEventCard
                {...event}
                eventButtons={eventButtons}
                isDesktop={matchMedia.lg}
                baseRequestUrl={baseRequestUrl}
                handleTagging={handleTagging}
                useCustomImage={recurringEvents.length === customImage}
              />
            </li>
          ))}
        </ul>
        {(!matchMedia.md && recurringJSONEvents.length > showMaxCount && !hideShowMoreButton) && (
          <Button
            variant="no-style"
            aria-label={`${title} ${showMoreLabel}`}
            className="AmexCenturionNYRecurringEvents__show-more"
            onClick={showMoreEvents}
          >
            {showMoreLabel}
          </Button>
        )}
        {(matchMedia.md && finePrint) && renderFinePrintLine()}
      </>
    );
  };

  const renderEventCarousel = () => (
    <CarouselProvider
      isIntrinsicHeight
      naturalSlideWidth={400}
      naturalSlideHeight={650}
      totalSlides={recurringJSONEvents.length}
      dragEnabled={false}
      step={slidesNumber}
      visibleSlides={slidesNumber}
    >
      <div
        aria-label={srTitle}
        role="region"
        aria-roledescription="carousel"
        className="AmexCenturionNYRecurringEvents__carousel"
      >
        {srInfo && (
          <p className="sr-only">
            {srInfo}
          </p>
        )}
        <Slider tabIndex={-1}>
          {recurringJSONEvents.map((event, index) => (
            <Slide
              index={index}
              key={event.id}
              aria-label={`${index + 1} of ${recurringJSONEvents.length}`}
              aria-roledescription="slide"
            >
              <AmexCenturionEventCard
                {...event}
                eventButtons={eventButtons}
                isDesktop={matchMedia.lg}
                baseRequestUrl={baseRequestUrl}
                handleTagging={handleTagging}
              />
            </Slide>
          ))}
        </Slider>
        <div className="AmexCenturionNYRecurringEvents__carousel--footer">
          {(matchMedia.md && finePrint) && renderFinePrintLine()}
          {renderButtons()}
        </div>
      </div>
    </CarouselProvider>
  );

  return (
    <section className="AmexCenturionNYRecurringEvents">
      <div className="container">
        {title && (
          <h2
            id="recurringEvents"
            className="AmexCenturionNYRecurringEvents__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedTitle,
            }}
          />
        )}
        {subTitle && (
          <h3
            className="AmexCenturionNYRecurringEvents__sub-title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSubTitle,
            }}
          />
        )}
        {infoCopy && (
          <>
            <hr role="presentation" />
            <p
              className="AmexCenturionNYRecurringEvents__info"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedInfoCopy,
              }}
            />
          </>
        )}
        {finePrintInfoCopy && (
          <>
            <p
              className="AmexCenturionNYRecurringEvents__fine-print"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedFinePrintInfoCopy,
              }}
            />
          </>
        )}
        {(!matchMedia.md && finePrint) && renderFinePrintLine()}
        {(matchMedia.md && recurringJSONEvents.length > 3) ? renderEventCarousel() : renderEventGrid()}
      </div>
    </section>
  );
};

AmexCenturionNYRecurringEvents.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  eventButtons: PropTypes.shape({}).isRequired,
  eventDetails: PropTypes.shape({
    recurring: PropTypes.shape({
      title: PropTypes.string.isRequired,
      sub_title: PropTypes.string.isRequired,
      info_copy: PropTypes.string.isRequired,
      fine_print: PropTypes.string.isRequired,
      sr_title: PropTypes.string.isRequired,
      sr_info: PropTypes.string.isRequired,
      sr_controls_aria: PropTypes.string.isRequired,
      show_max_count: PropTypes.number.isRequired,
      show_more: PropTypes.number.isRequired,
      more_button_label: PropTypes.string.isRequired,
      active_button: PropTypes.string.isRequired,
      number_of_events: PropTypes.shape({
        custom_image: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  recurringJSONEvents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  handleTagging: PropTypes.func.isRequired,
};

export default AmexCenturionNYRecurringEvents;
